import React, { useState, useCallback, useEffect } from "react";
import LVWorkflow from "./LVWorkflow";
import ZDocumentWorkflow from "./ZDocumentWorkflow";
import TakeoffWorkflow from "./TakeoffWorkflow";
import SavedRunView from "./SavedRunView";
import ErrorBoundary from "./ErrorBoundary";
import { useJobsContext } from "../contexts/JobsProvider";
import useJobWaiter from "../hooks/useJobWaiter";
import { isRunningStatus } from "../utils/jobStatus";
import { AGENT_LABELS } from "../utils/agentLabels";
import "./Workflow.css";

const WF_IDS = { APPROVAL: "WF-01", LV_MATCHING: "WF-02", TAKEOFF: "WF-03" };
const WF02_BTN_DEFAULT = "Start →";

const INITIAL_WORKFLOWS = [
  {
    id: WF_IDS.APPROVAL,
    name: "Approval Analysis",
    description:
      "Upload Z-17x document → structured application conditions, restrictions & norm references in minutes.",
    status: "idle",
    button: "Upload Z-17 Document",
    progressText: "Idle",
  },
  {
    id: WF_IDS.LV_MATCHING,
    name: "LV Matching",
    description:
      "80-100 page bill of quantities → match list against Lücking catalog with ≤/> logic.",
    status: "idle",
    button: WF02_BTN_DEFAULT,
    progressText: "Idle",
  },
  {
    id: WF_IDS.TAKEOFF,
    name: "Plan Quantity Takeoff",
    description:
      "Floor plans + sections → m² masonry and slabs with opening deductions.",
    status: "idle",
    button: "Start →",
    progressText: "Idle",
  },
];

// Map a background job (its result + params) onto the run-shape SavedRunView
// renders, so completed jobs reuse the exact same read-only renderers (DRY).
function jobToRun(job) {
  if (!job) return null;
  const params = job.params || {};
  const lvDocId = params.lv_document_id || params.document_id || "";
  return {
    status: job.status,
    error: job.error,
    agent_type: job.agent_type,
    project_name: job.project_name,
    workflow_id: job.workflow_id,
    database: job.database,
    lv_document_id: lvDocId,
    report: job.result || {},
    verdict: true,
  };
}

export default function Workflow({
  database,
  onRunApproved,
  openedRunId,
  onCloseRun,
  openJob,
  onCloseJob,
  onRetryJob,
  selectedWorkflowId,
  discoverDocId = "",
  openTakeoffJob = null,
  onTakeoffReattached,
}) {
  const { jobs } = useJobsContext();
  const { waitFor } = useJobWaiter();
  const [workflows, setWorkflows] = useState(INITIAL_WORKFLOWS);
  const [activeWorkflow, setActiveWorkflow] = useState(null);
  // Live copy of an opened RUNNING job — awaited until it finishes so the panel
  // flips from "läuft…" to the result on its own.
  const [liveJob, setLiveJob] = useState(null);
  useEffect(() => {
    if (!openJob || openJob.status !== "running") {
      setLiveJob(null);
      return undefined;
    }
    let stop = false;
    waitFor(openJob.id)
      .then((j) => { if (!stop) setLiveJob(j); })
      .catch(() => {
        /* transient fetch error — the shared jobs feed will retry on its own */
      });
    return () => {
      stop = true;
    };
  }, [openJob, waitFor]);

  // Expand the workflow chosen from the sidebar.
  useEffect(() => {
    if (!selectedWorkflowId) return;
    setActiveWorkflow(selectedWorkflowId);
  }, [selectedWorkflowId]);

  const handleWF01StatusChange = useCallback((status, progressText) => {
    setWorkflows((prev) =>
      prev.map((wf) =>
        wf.id === WF_IDS.APPROVAL
          ? {
              ...wf,
              status,
              progressText,
              button:
                status === "idle" ? "Upload Z-17 Document" : "View Progress →",
            }
          : wf,
      ),
    );
  }, []);

  const handleWF02StatusChange = useCallback((status, progressText) => {
    setWorkflows((prev) =>
      prev.map((wf) =>
        wf.id === WF_IDS.LV_MATCHING
          ? {
              ...wf,
              status,
              progressText,
              button: status === "idle" ? WF02_BTN_DEFAULT : "View Progress →",
            }
          : wf,
      ),
    );
  }, []);

  const handleWF03StatusChange = useCallback((status, progressText) => {
    setWorkflows((prev) =>
      prev.map((wf) =>
        wf.id === WF_IDS.TAKEOFF
          ? {
              ...wf,
              status,
              progressText,
              button: status === "idle" ? "Start →" : "View Progress →",
            }
          : wf,
      ),
    );
  }, []);

  const handleCardClick = (wfId) => {
    setActiveWorkflow((prev) => (prev === wfId ? null : wfId));
  };

  // A saved run opened from the sidebar replaces the workflow area (read-only).
  // Rendered via the return value (after all hooks) to respect the Rules of Hooks.
  if (openedRunId) {
    return (
      <div className="workflow-container">
        <ErrorBoundary key={openedRunId} onReset={onCloseRun}>
          <SavedRunView runId={openedRunId} onClose={onCloseRun} />
        </ErrorBoundary>
      </div>
    );
  }

  // A completed/failed background job opened from the sidebar — render its result
  // through the same SavedRunView renderer used for saved runs.
  if (openJob) {
    const job = liveJob && liveJob.id === openJob.id ? liveJob : openJob;
    const label = AGENT_LABELS[job.agent_type] || job.agent_type;
    let body;
    if (job.status === "running") {
      body = (
        <div className="job-progress">
          <span className="job-progress-spin" />
          <h2>{label} läuft…</h2>
          <p className="job-progress-name">{job.project_name}</p>
          <p className="job-progress-hint">
            Die Analyse läuft im Hintergrund. Das Ergebnis erscheint hier
            automatisch, sobald es fertig ist — Sie können auch zu einem anderen
            Tab wechseln.
          </p>
          <button className="ds-btn ds-btn--secondary job-progress-close" onClick={onCloseJob}>
            Schließen
          </button>
        </div>
      );
    } else if (job.status === "done" && job.agent_type === "schema") {
      body = (
        <div className="job-progress">
          <h2>✓ {label} fertig</h2>
          <p className="job-progress-name">{job.project_name}</p>
          <p className="job-progress-hint">
            Öffnen Sie das Dokument über den ✅-Eintrag in der Seitenleiste, um
            fortzufahren.
          </p>
          <button className="ds-btn ds-btn--secondary job-progress-close" onClick={onCloseJob}>
            Schließen
          </button>
        </div>
      );
    } else {
      body = (
        <SavedRunView
          run={jobToRun(job)}
          onClose={onCloseJob}
          onRetry={onRetryJob}
        />
      );
    }
    return (
      <div className="workflow-container">
        <ErrorBoundary key={openJob.id} onReset={onCloseJob}>
          {body}
        </ErrorBoundary>
      </div>
    );
  }

  // The local `workflows` status resets to "idle" whenever this tab remounts. Derive
  // the takeoff (WF-03) card's status from the shared jobs instead, so a running
  // takeoff still reads "running" after navigating away and back.
  const takeoffRunning = jobs.find(
    (j) => j.agent_type === "takeoff" && isRunningStatus(j.status),
  );
  const displayWorkflows = workflows.map((wf) =>
    wf.id === WF_IDS.TAKEOFF && takeoffRunning && wf.status === "idle"
      ? {
          ...wf,
          status: "running",
          progressText: takeoffRunning.message || "Läuft…",
          button: "View Progress →",
        }
      : wf,
  );

  return (
    <div className="workflow-container">
      <div className="ds-page-header">
        <h1 className="ds-page-title">Agentic Workflows</h1>
        <p className="ds-page-subtitle">
          Automated engineering pipelines · Human review mandatory on all
          outputs
        </p>
      </div>

      <div className="workflow-grid">
        {displayWorkflows.map((wf) => (
          <div
            key={wf.id}
            className={`workflow-card ${wf.status}${activeWorkflow === wf.id ? " active-card" : ""}`}
          >
            <div className="workflow-card-id">{wf.id}</div>
            <h3 className="workflow-card-title">{wf.name}</h3>
            <p className="workflow-card-description">{wf.description}</p>
            <div className={`workflow-status ${wf.status}`}>
              <span className="workflow-status-dot" />
              {wf.progressText}
            </div>
            <button
              className={`ds-btn ${wf.status === "running" ? "ds-btn--secondary" : "ds-btn--primary"} workflow-action-btn ${wf.status}`}
              disabled={wf.status === "disabled"}
              onClick={() => handleCardClick(wf.id)}
            >
              {wf.button}
            </button>
          </div>
        ))}
      </div>

      {activeWorkflow === WF_IDS.APPROVAL && (
        <ZDocumentWorkflow
          database={database}
          onStatusChange={handleWF01StatusChange}
          onRunApproved={onRunApproved}
          initialDiscoverDoc={discoverDocId}
        />
      )}
      {activeWorkflow === WF_IDS.LV_MATCHING && (
        <LVWorkflow
          database={database}
          onStatusChange={handleWF02StatusChange}
          onRunApproved={onRunApproved}
        />
      )}
      {activeWorkflow === WF_IDS.TAKEOFF && (
        <TakeoffWorkflow
          onStatusChange={handleWF03StatusChange}
          reattachJob={openTakeoffJob}
          onReattached={onTakeoffReattached}
        />
      )}
    </div>
  );
}
